import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from '@gatsbyjs/reach-router'
import { Subscribe } from '@react-rxjs/core'
import React, { FC, useCallback } from 'react'

import { SanityParentProductFragment } from '../../../../graphql/gatsby'
import {
  ConfigurableProductDetailsFragment,
  SimpleProductDetailsFragment,
} from '../../../../graphql/magento'
import { Maybe, Money } from '../../../../graphql/search'
import useRetailPrices from '../../../../hooks/useRetailPrices'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import { useDispensaryContext } from '../../../../lib/dispensaries'
import { usePayUpfrontOption } from '../../../../lib/products/hooks/usePayUpfrontOption'
import { useTestKitDelayedBilling } from '../../../../lib/products/hooks/useTestKitDelayedBilling'
import {
  findVariantInProductQuery,
  useQueryProductByUrlKeyVaryByAuth,
} from '../../../../lib/products/m2-products-by-url-key'
import { imageUrlFor } from '../../../../utils/image-url'
import { displayAttribute } from '../../../../utils/itemTools'
import { formatAsCurrency, itemPrice } from '../../../../utils/priceTools'
import { StyledFunctionComponent } from '../../../types'
import { TypographyShimmer } from '../../../TypographyShimmer'
import * as Styled from './styled'

export interface ItemHeadingProps {
  className?: string
  item: ConfigurableProductDetailsFragment
  sanityParentProduct?: SanityParentProductFragment | null
  selectedVariant: SimpleProductDetailsFragment | null | undefined
  retailPrice: Maybe<Money> | undefined
}

/**
 * Display variant price when authentication state settled
 */
export const VariantPriceReactive: StyledFunctionComponent<{
  item: ConfigurableProductDetailsFragment
  selectedVariant: SimpleProductDetailsFragment | null | undefined
  retailPrice: Maybe<Money> | undefined
}> = ({ className, item, selectedVariant, retailPrice }) => {
  const location = useLocation()
  const getCurrentPath = useCallback(() => `${location.pathname}${location.search}`, [location])
  const isEScriptPage = getCurrentPath().includes('/escript/build')
  const payUpfrontOption = usePayUpfrontOption(selectedVariant)
  const testKitDelayedBilling = useTestKitDelayedBilling(selectedVariant)
  const { shouldDisplayRetailPrice } = useRetailPrices()
  const result = useQueryProductByUrlKeyVaryByAuth(item.url_key ?? '')
  const itemId = item.id
  const sku = selectedVariant?.sku

  const variantForAuth =
    itemId == null || !sku || result._tag === 'Left'
      ? undefined
      : findVariantInProductQuery(result.data, { itemId, sku })

  const retailPriceAmount =
    retailPrice?.amount && shouldDisplayRetailPrice
      ? formatAsCurrency({ amount: parseFloat(retailPrice.amount) })
      : undefined
  const price = variantForAuth
    ? itemPrice({
        item: variantForAuth.product,
        ignoreFalsyAmount: !(testKitDelayedBilling && !!retailPriceAmount),
      })
    : undefined
  // Do not display price if it's null or if both the pay upfront option is available
  // and the current path is not for the eScript builder
  return price == null || (!!payUpfrontOption && !isEScriptPage) ? null : (
    <Styled.PriceBox className={className}>
      <Styled.Prices>
        {isEScriptPage && retailPriceAmount ? (
          <Styled.Retail $isOnlyPrice={true}>
            <Styled.RetailPrice>{retailPriceAmount}</Styled.RetailPrice> retail
          </Styled.Retail>
        ) : (
          <>
            <Styled.Price>{price}</Styled.Price>
            {!testKitDelayedBilling && retailPriceAmount && (
              <Styled.Retail>
                <Styled.RetailPrice>{retailPriceAmount}</Styled.RetailPrice> retail
              </Styled.Retail>
            )}
          </>
        )}
      </Styled.Prices>
      {testKitDelayedBilling && retailPriceAmount && !isEScriptPage && (
        <Styled.DelayedBilling>
          <Styled.DelayedBillingPrice>{retailPriceAmount}</Styled.DelayedBillingPrice>
          <Styled.DelayedBillingMessage>
            {' '}
            to be charged when a sample is processed by Diagnostic Solutions Lab
          </Styled.DelayedBillingMessage>{' '}
        </Styled.DelayedBilling>
      )}
    </Styled.PriceBox>
  )
}

/**
 * Loading component for variant price
 */
export const VariantPriceSkeleton: FC = () => (
  <Styled.PriceBox>
    <Styled.Price>
      <TypographyShimmer $width="6em" />
    </Styled.Price>
  </Styled.PriceBox>
)

const ItemHeading: FC<ItemHeadingProps> = ({
  className,
  item,
  sanityParentProduct,
  selectedVariant = null,
  retailPrice,
}) => {
  const npn = displayAttribute({ item, selectedVariant, attribute: 'natural_product_number' })
  const tagline = sanityParentProduct?.tagline ?? undefined
  const headerImage = sanityParentProduct?.headerImage?.asset
    ? imageUrlFor(sanityParentProduct.headerImage).url()
    : undefined
  const { featureFlags } = useSiteMetadata() ?? {}
  const { isAuthenticated } = useAuth0()
  const dispensary = useDispensaryContext()
  const anonymousCart = featureFlags?.anonymousCart ?? false
  const showPrice = isAuthenticated || !!dispensary || anonymousCart

  return (
    <Styled.Container className={className}>
      {headerImage && <Styled.HeaderImage src={headerImage} />}
      <div className="robotoCondensed sku">
        SKU: {displayAttribute({ item, selectedVariant, attribute: 'sku' })?.toUpperCase()}
        <span className="npn">{npn ? `NPN: ${npn}` : ''}</span>
      </div>
      <h1 className="item-name">{item.name}</h1>
      {tagline && <Styled.Tagline>{tagline}</Styled.Tagline>}
      {showPrice && (
        <Subscribe fallback={<VariantPriceSkeleton />}>
          <VariantPriceReactive
            item={item}
            selectedVariant={selectedVariant}
            retailPrice={retailPrice}
          />
        </Subscribe>
      )}
    </Styled.Container>
  )
}

export default ItemHeading
